import React, { useState } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import {
  ChevronLeft,
  Lock,
  CreditCard,
  Check,
} from "lucide-react";
import { useCart, formatEuro } from "../context/CartContext";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Pagina Checkout — /checkout.
 *
 * Flusso:
 *   1. Dati cliente (nome, cognome, email, telefono opzionale, paese)
 *   2. Riepilogo ordine
 *   3. Metodo di pagamento: Carta (Stripe Checkout hosted)
 *   4. "Conferma ordine" → crea Session Stripe → redirect a checkout Stripe
 *
 * L'ordine NON viene mai confermato prima che Stripe restituisca
 * `payment_status = paid` (vedi PaymentSuccessPage).
 */
export default function CheckoutPage() {
  const { items, subtotal, totalQuantity } = useCart();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("Italia");
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Cart vuoto → torna al carrello (a meno che sia in corso il redirect a Stripe).
  if (items.length === 0 && !submitting) {
    return <Navigate to="/carrello" replace />;
  }

  const missingLookupKey = items.find((it) => !it.stripeLookupKey);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!firstName.trim() || !lastName.trim()) {
      setError("Inserisci nome e cognome.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Inserisci un'email valida.");
      return;
    }
    if (!terms) {
      setError("Devi accettare Termini e Privacy per continuare.");
      return;
    }
    if (missingLookupKey) {
      setError("Uno dei prodotti nel carrello non è ancora acquistabile online.");
      return;
    }

    setSubmitting(true);
    try {
      // Persisti dati cliente per la pagina di conferma post-pagamento.
      window.sessionStorage.setItem(
        "time-inside:pending-customer",
        JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          country,
        })
      );

      const { data } = await axios.post(`${API}/api/payments/checkout`, {
        items: items.map((it) => ({
          lookup_key: it.stripeLookupKey,
          quantity: it.quantity,
        })),
        origin_url: window.location.origin,
        customer_email: email.trim(),
        customer_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
      });

      if (!data?.checkout_url) {
        throw new Error("URL di checkout mancante dalla risposta.");
      }
      // Redirect al pagamento Stripe hosted (nessun dato carta nel FE).
      window.location.href = data.checkout_url;
    } catch (err) {
      setSubmitting(false);
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Impossibile avviare il pagamento. Riprova tra un momento.";
      setError(msg);
    }
  };

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 max-w-5xl mx-auto">
      <Link
        to="/carrello"
        data-testid="checkout-back-link"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/40 hover:text-white/80 transition mb-10"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Torna al carrello
      </Link>

      <div className="mb-10">
        <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-400/80 mb-3">
          TIME Inside · Checkout
        </div>
        <h1
          data-testid="checkout-title"
          className="text-4xl sm:text-5xl font-extralight tracking-tight text-white leading-[1.05]"
        >
          Un ultimo passo.
        </h1>
        <p className="mt-3 text-white/50 font-light">
          Completa l'ordine. Il pagamento è protetto e gestito da Stripe.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="grid md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-8 lg:gap-12 items-start"
      >
        <div className="space-y-10">
          {/* Dati cliente */}
          <section data-testid="checkout-customer">
            <SectionEyebrow>1 · Dati cliente</SectionEyebrow>
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <Field
                label="Nome"
                testId="checkout-firstName"
                value={firstName}
                onChange={setFirstName}
                required
              />
              <Field
                label="Cognome"
                testId="checkout-lastName"
                value={lastName}
                onChange={setLastName}
                required
              />
              <Field
                label="Email"
                testId="checkout-email"
                type="email"
                value={email}
                onChange={setEmail}
                required
                className="sm:col-span-2"
              />
              <Field
                label="Telefono (facoltativo)"
                testId="checkout-phone"
                value={phone}
                onChange={setPhone}
              />
              <div>
                <label className="block text-[10px] tracking-[0.24em] uppercase text-white/45 font-mono mb-2">
                  Paese
                </label>
                <select
                  data-testid="checkout-country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white focus:border-emerald-400/40 focus:outline-none transition-colors"
                >
                  <option value="Italia">Italia</option>
                  <option value="Svizzera">Svizzera</option>
                  <option value="San Marino">San Marino</option>
                  <option value="Altro">Altro paese UE</option>
                </select>
              </div>
            </div>
          </section>

          {/* Metodo di pagamento */}
          <section data-testid="checkout-payment">
            <SectionEyebrow>2 · Metodo di pagamento</SectionEyebrow>
            <div className="mt-5">
              <div
                data-testid="pay-card"
                className="text-left p-4 rounded-xl border border-emerald-400/40 bg-emerald-400/[0.06]"
              >
                <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-mono text-emerald-300">
                  <CreditCard className="w-4 h-4" />
                  Carta · Stripe
                </div>
                <div className="mt-1 text-[10px] text-white/40 font-mono">
                  Visa · Mastercard · Amex · Google Pay · Apple Pay
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-start gap-3 px-4 py-3 rounded-xl bg-emerald-500/[0.05] border border-emerald-400/15 text-[12px] text-emerald-200/80 font-light">
              <Lock className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Verrai reindirizzato a Stripe per completare il pagamento in
                modo sicuro. Nessun dato di carta transita da TIME Inside.
              </span>
            </div>
          </section>

          {/* Terms */}
          <section>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                data-testid="checkout-terms"
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-white/20 bg-black/50"
              />
              <span className="text-[12px] text-white/60 font-light leading-relaxed">
                Accetto i{" "}
                <Link to="/termini" className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2">
                  Termini
                </Link>
                {" "}e la{" "}
                <Link to="/privacy" className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2">
                  Privacy Policy
                </Link>
                {" "}del progetto TIME.
              </span>
            </label>
          </section>

          {error && (
            <div
              data-testid="checkout-error"
              className="text-[12px] text-red-300 bg-red-500/[0.08] border border-red-500/20 rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}
        </div>

        {/* Riepilogo ordine */}
        <aside
          data-testid="checkout-summary"
          className="rounded-2xl bg-white/[0.02] border border-white/5 p-6 sm:p-7 md:sticky md:top-24"
        >
          <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-400/70 mb-4">
            Riepilogo ordine
          </div>
          <ul className="space-y-3">
            {items.map((it) => (
              <li
                key={it.slug}
                data-testid={`checkout-item-${it.slug}`}
                className="flex items-start justify-between gap-3 text-sm"
              >
                <div className="min-w-0">
                  <div className="text-white/85 font-light truncate">
                    {it.title}
                    {it.subtitle ? ` — ${it.subtitle}` : ""}
                  </div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 font-mono mt-0.5">
                    × {it.quantity}
                    {it.format ? ` · ${it.format}` : ""}
                  </div>
                </div>
                <div className="text-amber-400 font-mono tabular-nums shrink-0">
                  {formatEuro(it.price * it.quantity)}
                </div>
              </li>
            ))}
          </ul>
          <div className="my-5 h-px bg-white/10" />
          <dl className="space-y-2 text-sm">
            <div className="flex items-center justify-between text-white/70 font-light">
              <dt>Subtotale ({totalQuantity})</dt>
              <dd className="tabular-nums text-white">{formatEuro(subtotal)}</dd>
            </div>
            <div className="flex items-center justify-between text-white/50 font-light">
              <dt>Consegna digitale</dt>
              <dd className="tabular-nums">Gratuita</dd>
            </div>
          </dl>
          <div className="my-5 h-px bg-white/10" />
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] tracking-[0.32em] uppercase text-white/50 font-mono">
              Totale
            </span>
            <span
              data-testid="checkout-total"
              className="text-2xl sm:text-3xl text-amber-400 font-light tabular-nums"
              style={{ textShadow: "0 0 20px rgba(212,175,55,0.3)" }}
            >
              {formatEuro(subtotal)}
            </span>
          </div>

          <button
            data-testid="checkout-confirm-btn"
            type="submit"
            disabled={submitting}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-emerald-400 text-black hover:bg-emerald-300 disabled:opacity-60 transition-colors text-[11px] tracking-[0.28em] uppercase font-medium shadow-[0_10px_40px_-10px_rgba(16,185,129,0.6)]"
          >
            <Check className="w-4 h-4" />
            {submitting ? "Reindirizzamento…" : "Conferma ordine"}
          </button>

          <p className="mt-4 text-[10px] tracking-[0.24em] uppercase text-white/30 text-center font-mono">
            <Lock className="inline w-3 h-3 mr-1" />
            Pagamento sicuro · Stripe
          </p>
        </aside>
      </form>
    </div>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-400/70">
      {children}
    </div>
  );
}

function Field({ label, testId, value, onChange, type = "text", required = false, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] tracking-[0.24em] uppercase text-white/45 font-mono mb-2">
        {label}{required && <span className="text-emerald-400 ml-1">*</span>}
      </span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white placeholder-white/25 focus:border-emerald-400/40 focus:outline-none transition-colors"
      />
    </label>
  );
}
